import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FileCheck2, Hourglass, Loader2, Paperclip, X } from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { api, formatErr } from "@/lib/api";
import { useTour } from "@/components/tour/TourProvider";

function left(deadline, t) {
  const [y, m, d] = deadline.split("-").map(Number);
  const ms = new Date(y, m - 1, d, 23, 59, 59) - t;
  if (ms <= 0) return null;
  return { d: Math.floor(ms / 86400000), h: Math.floor((ms / 3600000) % 24), m: Math.floor((ms / 60000) % 60), s: Math.floor((ms / 1000) % 60) };
}

function Proof({ x, i, a, onSet }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (f) => {
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { toast.error("File too large (max 10 MB)"); return; }
    setBusy(true);
    const fd = new FormData(); fd.append("file", f);
    try { const { data } = await api.post(`/lease-loi/${x.loi_id}/approvals/${i}/proof`, fd); onSet(x.loi_id, data.approvals); toast.success("Proof attached and approval marked done"); }
    catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  const view = () => api.get(`/lease-loi/${x.loi_id}/approvals/${i}/proof`, { responseType: "blob" }).then((r) => window.open(URL.createObjectURL(r.data), "_blank")).catch(() => toast.error("Couldn't open the file"));
  const remove = () => window.confirm("Remove this proof file?") && api.delete(`/lease-loi/${x.loi_id}/approvals/${i}/proof`).then((r) => onSet(x.loi_id, r.data.approvals)).catch((e) => toast.error(formatErr(e)));
  if (a.proof) return (
    <span className="inline-flex items-center gap-1 shrink-0">
      <button onClick={view} title={a.proof.name} className="text-xs text-emerald-700 hover:underline inline-flex items-center gap-1" data-testid={`countdown-proof-view-${x.loi_id}-${i}`}><FileCheck2 className="h-3.5 w-3.5" />Proof</button>
      <button onClick={remove} aria-label="Remove proof" className="text-slate-400 hover:text-rose-600" data-testid={`countdown-proof-remove-${x.loi_id}-${i}`}><X className="h-3.5 w-3.5" /></button>
    </span>);
  return (
    <>
      <input ref={ref} type="file" accept="application/pdf,image/jpeg,image/png" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid={`countdown-proof-input-${x.loi_id}-${i}`} />
      <button onClick={() => ref.current?.click()} disabled={busy} aria-label="Attach proof" title="Attach permit, letter or license (PDF, JPG, PNG)" className="text-slate-400 hover:text-emerald-700 shrink-0" data-testid={`countdown-proof-attach-${x.loi_id}-${i}`}>{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Paperclip className="h-3.5 w-3.5" />}</button>
    </>);
}

function Row({ x, t, onTick, onSet }) {
  const { lang } = useTour();
  const l = left(x.deadline, t);
  const done = x.approvals.filter((a) => a.done).length;
  const urgent = l && l.d < 30 && done < x.approvals.length;
  return (
    <div className="py-3" data-testid={`countdown-${x.loi_id}`}>
      <div className="flex items-baseline gap-3 flex-wrap"><span className="font-medium text-slate-900 flex-1 min-w-0 truncate">{x.address}</span>
        <span className={`font-mono text-sm font-bold ${!l ? "text-slate-400" : urgent ? "text-rose-700" : "text-slate-900"}`} data-testid={`countdown-time-${x.loi_id}`}>{l ? `${l.d}d ${String(l.h).padStart(2, "0")}h ${String(l.m).padStart(2, "0")}m ${String(l.s).padStart(2, "0")}s` : "Contingency ended"}</span></div>
      <div className="text-xs text-slate-500">Contingency ends {new Date(`${x.deadline}T12:00:00`).toLocaleDateString(lang === "es" ? "es-US" : undefined, { month: "long", day: "numeric", year: "numeric" })} · {done}/{x.approvals.length} approvals</div>
      <ul className="mt-2 grid sm:grid-cols-2 gap-1.5">{x.approvals.map((a, i) => (
        <li key={i} className="flex items-center gap-2"><label className="flex items-center gap-2 text-sm cursor-pointer flex-1 min-w-0"><Checkbox checked={a.done} onCheckedChange={(v) => onTick(x.loi_id, i, !!v)} data-testid={`countdown-approval-${x.loi_id}-${i}`} /><span className={a.done ? "line-through text-slate-400" : "text-slate-700"}>{a.label}</span></label><Proof x={x} i={i} a={a} onSet={onSet} /></li>))}</ul>
    </div>
  );
}

export function ContingencyCard() {
  const [rows, setRows] = useState([]);
  const [t, setT] = useState(Date.now());
  useEffect(() => { api.get("/lease-loi/countdown/all").then((r) => setRows(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (!rows.length) return undefined; const i = setInterval(() => setT(Date.now()), 1000); return () => clearInterval(i); }, [rows.length]);
  if (!rows.length) return null;
  const tick = (id, i, done) => {
    setRows((rs) => rs.map((x) => (x.loi_id === id ? { ...x, approvals: x.approvals.map((a, j) => (j === i ? { ...a, done } : a)) } : x)));
    api.put(`/lease-loi/${id}/approvals/${i}`, { done }).catch((e) => toast.error(formatErr(e)));
  };
  const setAp = (id, approvals) => setRows((rs) => rs.map((x) => (x.loi_id === id ? { ...x, approvals } : x)));
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 mb-6" data-testid="contingency-card">
      <div className="flex items-center gap-2"><Hourglass className="h-5 w-5 text-emerald-600" /><h3 className="font-semibold text-slate-900 flex-1">Lease contingency countdown</h3>
        <Link to="/dashboard/lease-loi" className="text-xs text-emerald-700 hover:underline" data-testid="contingency-open-loi">Lease LOI Builder</Link></div>
      <p className="text-xs text-slate-500 mt-1">Get every approval in before the contingency ends. After that date, the lease can no longer be cancelled risk-free. Use the paperclip to attach the permit, zoning letter or license as proof.</p>
      <div className="divide-y divide-slate-100">{rows.map((x) => <Row key={x.loi_id} x={x} t={t} onTick={tick} onSet={setAp} />)}</div>
    </div>
  );
}
